import React, { useState } from 'react';
import styled from 'styled-components';
import { BaseModal } from './BaseModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Color } from 'core';
import { primaryGreen, primaryGray, warningOrange, hexToRGB } from 'core/colors';
import { useTranslation } from 'react-i18next';
import { ConfirmOrCancel } from 'components/forms/ConfirmOrCancel';
import { UserRole } from 'types';
import { CONTENT_CREATOR_ROLES } from 'utils/constants';

interface IProps {
  onClose: () => void;
  onConfirm: (option: 'mirror' | 'hardCopy') => void;
  isSurveySelected: boolean;
  userRole?: UserRole;
  isSiteAdmin?: boolean;
}

export const AddSharedAssignmentModal = (props: IProps) => {
  const { onClose, onConfirm, isSurveySelected, userRole, isSiteAdmin } = props;
  const { t } = useTranslation();
  // If it's a survey, default to hardCopy since mirror is disabled
  const [selectedOption, setSelectedOption] = useState<'mirror' | 'hardCopy'>(isSurveySelected ? 'hardCopy' : 'mirror');
  const [showAdvanced, setShowAdvanced] = useState(isSurveySelected); // Auto-expand if survey

  // Check if user has permission for hard copy
  const canUseHardCopy = isSiteAdmin || CONTENT_CREATOR_ROLES.includes(userRole as UserRole);

  const handleConfirm = () => {
    onConfirm(selectedOption);
  };

  const handleMirrorClick = () => {
    if (!isSurveySelected) {
      setSelectedOption('mirror');
    }
  };

  const handleHardCopyClick = () => {
    if (canUseHardCopy) {
      setSelectedOption('hardCopy');
    }
  };

  return (
    <BaseModal title={t('Add to my course')} onClose={onClose}>
      <ModalBody>
        <Subtitle>{t('This will add the shared assignment to your course')}</Subtitle>

        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend className="sr-only">
          {t('Add to my course')}
        </legend>
        {/* Mirror option - always visible and recommended */}
        <OptionContainer selected={selectedOption === 'mirror'} disabled={isSurveySelected} onClick={handleMirrorClick}>
          {!isSurveySelected && <RecommendedBadge>{t('RECOMMENDED')}</RecommendedBadge>}
          <OptionContent>
            <RadioContainer>
              <RadioInput
                type="radio"
                id="mirror"
                name="addOption"
                value="mirror"
                checked={selectedOption === 'mirror'}
                onChange={() => setSelectedOption('mirror')}
                disabled={isSurveySelected}
              />
              <RadioLabel htmlFor="mirror">
                <OptionTitle>{t('Mirror changes')}</OptionTitle>
              </RadioLabel>
            </RadioContainer>
            <OptionDescription>
              <DescriptionHighlight>{t('Keeps assignment in sync.')}</DescriptionHighlight>{' '}
              {t('When the owner updates content, it automatically appears in your course. Best for most cases.')}
              {isSurveySelected && <DisabledWarning>{t('Not available for surveys')}</DisabledWarning>}
            </OptionDescription>
          </OptionContent>
        </OptionContainer>

        {/* Advanced options section */}
        <AdvancedSection>
          <AdvancedToggle onClick={() => setShowAdvanced(!showAdvanced)} aria-expanded={showAdvanced}>
            <FontAwesomeIcon
              icon={showAdvanced ? faChevronDown : faChevronRight}
              style={{ marginRight: '8px', fontSize: '12px' }}
            />
            <AdvancedText>{t('Advanced options')}</AdvancedText>
          </AdvancedToggle>

          <AdvancedContent isExpanded={showAdvanced}>
            <OptionContainer
              selected={selectedOption === 'hardCopy'}
              disabled={!canUseHardCopy}
              onClick={handleHardCopyClick}
            >
              <OptionContent>
                <RadioContainer>
                  <RadioInput
                    type="radio"
                    id="hardCopy"
                    name="addOption"
                    value="hardCopy"
                    checked={selectedOption === 'hardCopy'}
                    onChange={() => setSelectedOption('hardCopy')}
                    disabled={!canUseHardCopy}
                  />
                  <RadioLabel htmlFor="hardCopy">
                    <OptionTitle>{t('Hard copy this assignment')}</OptionTitle>
                  </RadioLabel>
                </RadioContainer>
                <OptionDescription>
                  <WarningText>{t('Only if you need to make different changes.')}</WarningText>{' '}
                  {t("Creates an independent copy you can edit, but you won't receive updates from the owner.")}
                  {isSurveySelected && <RequiredWarning>{t('Required for surveys')}</RequiredWarning>}
                  {!canUseHardCopy && (
                    <DisabledWarning>
                      {t('Only available for admins, course managers, and content managers')}
                    </DisabledWarning>
                  )}
                </OptionDescription>
              </OptionContent>
            </OptionContainer>
          </AdvancedContent>
        </AdvancedSection>
        </fieldset>

        <ButtonWrapper>
          <ConfirmOrCancel
            confirmText={t('Add')}
            handleConfirmClick={handleConfirm}
            confirmIsDisabled={isSurveySelected && selectedOption === 'mirror'}
            confirmIsPending={false}
            handleCancelClick={onClose}
            cancelText={t('Cancel')}
          />
        </ButtonWrapper>
      </ModalBody>
    </BaseModal>
  );
};

// Styled components
const ModalBody = styled.div`
  width: 600px;
  padding: 8px;
`;

const Subtitle = styled.p`
  color: ${Color.darkGray};
  margin: 0 0 24px 0;
  font-size: 16px;
`;

const OptionContainer = styled.div<{ selected: boolean; disabled?: boolean }>`
  border: 2px solid ${(props) => (props.selected ? primaryGreen : Color.lightGray)};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  position: relative;
  background: ${(props) => {
    if (props.disabled) return primaryGray;
    return props.selected ? hexToRGB(primaryGreen, 0.05) : 'white';
  }};
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};
  transition: all 0.2s ease;

  &:hover:not([disabled]) {
    border-color: ${primaryGreen};
    background: ${(props) => (props.disabled ? primaryGray : hexToRGB(primaryGreen, 0.05))};
  }
`;

const RecommendedBadge = styled.span`
  position: absolute;
  top: -12px;
  right: 20px;
  background: ${primaryGreen};
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const RadioContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const RadioInput = styled.input`
  appearance: none;
  -webkit-appearance: none;
  margin-right: 12px;
  margin-top: 2px;
  width: 18px;
  height: 18px;
  border: 2px solid ${Color.lightGray};
  border-radius: 50%;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;

  &:checked {
    border-color: ${primaryGreen};
    background-color: ${primaryGreen};
  }

  &:checked::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: white;
  }

  &:hover:not(:disabled) {
    border-color: ${primaryGreen};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background-color: ${primaryGray};
  }
`;

const RadioLabel = styled.label`
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const OptionTitle = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${Color.primaryBlack};
`;

const OptionDescription = styled.div`
  margin-left: 30px;
  color: ${Color.darkGray};
  font-size: 15px;
  line-height: 1.5;
`;

const DescriptionHighlight = styled.span`
  color: ${primaryGreen};
  font-weight: 600;
`;

const WarningText = styled.span`
  color: ${warningOrange};
  font-weight: 600;
`;

const DisabledWarning = styled.div`
  color: ${Color.red};
  font-weight: 600;
  margin-top: 8px;
  font-size: 14px;
`;

const RequiredWarning = styled.div`
  color: ${warningOrange};
  font-weight: 600;
  margin-top: 8px;
  font-size: 14px;
`;

const AdvancedSection = styled.div`
  margin-top: 24px;
`;

const AdvancedContent = styled.div<{ isExpanded: boolean }>`
  max-height: ${(props) => (props.isExpanded ? '300px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`;

const AdvancedToggle = styled.button`
  background: none;
  border: none;
  color: ${primaryGreen};
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0;
  margin-bottom: 16px;
  font-size: 15px;

  &:hover {
    text-decoration: underline;
  }
`;

const AdvancedText = styled.span`
  font-weight: 500;
`;

const ButtonWrapper = styled.div`
  margin-top: 32px;
  padding-top: 12px;
  border-top: 1px solid ${Color.lightGray};
`;
